import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useWS } from "@/contexts/WebSocketContext";
import { Button } from "@/components/ui/button";
import {
    Moon,
    Sun,
    LogOut,
    LayoutGrid,
    Radar,
    Users,
    CircleDot,
    Tags,
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Header() {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const { connected } = useWS();
    const location = useLocation();
    const navigate = useNavigate();
    const isAdmin = user && user !== false && user.role === "admin";

    const navItems = [
        { to: "/dashboard", label: "My Apps", icon: LayoutGrid, admin: false },
        { to: "/discovery", label: "Discovered", icon: Radar, admin: true },
        { to: "/categories", label: "Categories", icon: Tags, admin: true },
        { to: "/users", label: "Users", icon: Users, admin: true },
    ];

    return (
        <header className="glass-header sticky top-0 z-40">
            <div className="max-w-[1600px] mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Link to="/dashboard" className="flex items-center gap-2.5 group" data-testid="brand-link">
                        <div className="h-8 w-8 rounded-lg bg-foreground text-background flex items-center justify-center font-display font-bold">
                            A
                        </div>
                        <span className="font-display font-bold text-lg tracking-tight">AppDash</span>
                    </Link>
                    <div
                        className="hidden sm:flex items-center gap-1.5 ml-3 text-xs font-medium px-2.5 py-1 rounded-full"
                        title={connected ? "Realtime connected" : "Realtime disconnected"}
                        data-testid="ws-indicator"
                    >
                        <CircleDot
                            className={`h-3 w-3 ${connected ? "text-emerald-500" : "text-muted-foreground"}`}
                        />
                        <span className="text-muted-foreground">{connected ? "Live" : "Offline"}</span>
                    </div>
                </div>

                <nav className="hidden md:flex items-center gap-1">
                    {navItems
                        .filter((n) => !n.admin || isAdmin)
                        .map((n) => {
                            const Icon = n.icon;
                            const active = location.pathname.startsWith(n.to);
                            return (
                                <Link
                                    key={n.to}
                                    to={n.to}
                                    data-testid={`nav-${n.to.replace("/", "")}`}
                                    className={`px-3.5 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-colors ${
                                        active
                                            ? "bg-foreground text-background"
                                            : "text-muted-foreground hover:text-foreground hover:bg-foreground/5"
                                    }`}
                                >
                                    <Icon className="h-4 w-4" />
                                    {n.label}
                                </Link>
                            );
                        })}
                </nav>

                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={toggle}
                        data-testid="theme-toggle"
                        className="rounded-full"
                        aria-label="toggle theme"
                    >
                        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </Button>

                    {user && user !== false && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="rounded-full px-3 gap-2" data-testid="user-menu">
                                    <div className="h-7 w-7 rounded-full bg-foreground text-background flex items-center justify-center text-xs font-bold uppercase">
                                        {user.username?.[0] || "?"}
                                    </div>
                                    <span className="hidden sm:inline text-sm font-medium">{user.username}</span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                                    {user.role}
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={() => navigate("/change-password")}
                                    data-testid="menu-change-password"
                                >
                                    Change Password
                                </DropdownMenuItem>
                                {/* Mobile-only nav */}
                                <div className="md:hidden">
                                    <DropdownMenuSeparator />
                                    {navItems
                                        .filter((n) => !n.admin || isAdmin)
                                        .map((n) => (
                                            <DropdownMenuItem
                                                key={n.to}
                                                onClick={() => navigate(n.to)}
                                                data-testid={`menu-${n.to.replace("/", "")}`}
                                            >
                                                {n.label}
                                            </DropdownMenuItem>
                                        ))}
                                </div>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-500">
                                    <LogOut className="h-4 w-4 mr-2" /> Sign out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </div>
        </header>
    );
}
